import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiSearch, FiPlus, FiChevronDown, FiCheck } from 'react-icons/fi';
import { apiGet, apiPost } from '../utils/apiUtils';

const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

const CustomSelect = ({ value, onChange, options, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(
        options.find(opt => opt.value === value) || null
    );
    const [openUp, setOpenUp] = useState(false);
    const buttonRef = useRef(null);

    useEffect(() => {
        setSelectedOption(options.find(opt => opt.value === value) || null);
    }, [value, options]);

    const toggleDropdown = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (spaceBelow < 200 && spaceAbove > spaceBelow) {
                setOpenUp(true);
            } else {
                setOpenUp(false);
            }
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (option) => {
        setSelectedOption(option);
        onChange(option.value);
        setIsOpen(false);
    };

    return (
        <div className="relative w-full sm:w-40 shrink-0 z-50 text-sm font-sans">
            <button
                ref={buttonRef}
                type="button"
                onClick={toggleDropdown}
                className="w-full bg-accent text-foreground px-4 py-3 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all flex items-center justify-between"
            >
                <span className="text-foreground truncate mr-2 font-medium">
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <FiChevronDown className={`w-4 h-4 text-emerald-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div
                        className={`absolute left-0 right-0 mt-2 bg-white rounded z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in duration-200 shadow-xl border border-gray-200 divide-y divide-gray-300 ${openUp ? 'bottom-full mb-2' : 'top-full'}`}
                    >
                        {options.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => handleSelect(option)}
                                className={`w-full text-left px-4 py-3 text-sm transition-all ${value === option.value ? 'bg-emerald-600 text-white font-bold' : 'text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

/**
 * @component AssignPracticeProblemModal
 * @description Admin modal to search and assign coding problems into a batch Practice Arena.
 */
const AssignPracticeProblemModal = ({ batchId, onClose, onProblemAssigned }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [difficulty, setDifficulty] = useState('all');
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [assigning, setAssigning] = useState(false);

    const searchQuestions = async (query, diff) => {
        try {
            setLoading(true);
            setError(null);

            let url = '/questions?';
            if (query) url += `search=${encodeURIComponent(query)}&`;
            if (diff && diff !== 'all') url += `difficulty=${encodeURIComponent(diff)}&`;

            const response = await apiGet(url);
            if (!response.ok) {
                throw new Error('Failed to search questions');
            }
            const data = await response.json();

            if (data.questions) {
                setQuestions(data.questions);
            } else if (Array.isArray(data)) {
                setQuestions(data);
            } else {
                setQuestions([]);
            }

        } catch (err) {
            console.error('Search error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const debouncedSearch = useCallback(
        debounce((query, diff) => searchQuestions(query, diff), 400),
        []
    );

    useEffect(() => {
        debouncedSearch(searchQuery, difficulty);
    }, [searchQuery, difficulty, debouncedSearch]);

    const handleAssignProblem = async () => {
        if (!selectedQuestion) return;

        try {
            setAssigning(true);
            setError(null);

            const response = await apiPost(`/batches/${batchId}/practice-problems`, {
                question_id: selectedQuestion.id
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to assign problem');
            }

            const data = await response.json();
            onProblemAssigned(data.practiceProblem);

            setSelectedQuestion(null);
        } catch (err) {
            console.error('Assign error:', err);
            setError(err.message);
        } finally {
            setAssigning(false);
        }
    };

    const getDifficultyColor = (diff) => {
        switch (diff?.toLowerCase()) {
            case 'easy': return 'text-green-600';
            case 'medium': return 'text-amber-600';
            case 'hard': return 'text-red-600';
            default: return 'text-slate-600';
        }
    };

    const difficultyOptions = [
        { value: 'all', label: 'All Levels' },
        { value: 'easy', label: 'Easy' },
        { value: 'medium', label: 'Medium' },
        { value: 'hard', label: 'Hard' }
    ];

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white rounded w-full max-w-7xl mx-auto my-8 flex flex-col h-[85vh] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">

                {/* Header */}
                <div className="bg-emerald-600 px-6 py-4 flex items-center justify-between shrink-0">
                    <h2 className="text-xl font-bold text-white mb-0.5">Assign Practice Problem</h2>
                    <button
                        onClick={onClose}
                        className="text-emerald-100 hover:text-white transition-colors mt-1"
                    >
                        <FiX className="text-2xl" />
                    </button>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Left Panel: Search & Results */}
                    <div className="flex-1 flex flex-col border-r border-gray-300 overflow-hidden pb-4">

                        {/* Search Bar */}
                        <div className="p-8 shrink-0 space-y-4">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="relative flex-1 group">
                                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" />
                                    <input
                                        type="text"
                                        placeholder="Search by title, topic..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full bg-accent border-b-2 border-transparent rounded-t text-foreground pl-11 pr-4 py-3 focus:outline-none focus:border-emerald-600 transition-colors text-sm font-medium placeholder:text-slate-400"
                                    />
                                </div>
                                <CustomSelect
                                    value={difficulty}
                                    onChange={setDifficulty}
                                    options={difficultyOptions}
                                    placeholder="Difficulty"
                                />
                            </div>
                        </div>

                        {/* Search Results */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar border-l-4 border-emerald-600 rounded mx-8 bg-accent pr-2">
                            {loading ? (
                                <div className="divide-y divide-gray-300 animate-pulse">
                                    {[1, 2, 3, 4, 5, 6].map((i) => (
                                        <div key={i} className="px-4 py-4 flex items-center space-x-4">
                                            <div className="flex-1 space-y-3">
                                                <div className="h-4 bg-gray-200 rounded w-3/4" />
                                                <div className="flex space-x-3">
                                                    <div className="h-3 bg-gray-200 rounded w-16" />
                                                    <div className="h-3 bg-gray-200 rounded w-20" />
                                                </div>
                                            </div>
                                            <div className="w-8 h-8 bg-gray-100 rounded" />
                                        </div>
                                    ))}
                                </div>
                            ) : questions.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                                    <div className="w-16 h-16 flex items-center justify-center mb-4">
                                        <FiSearch className="text-2xl text-muted-foreground" />
                                    </div>
                                    <h4 className="font-bold text-slate-600 tracking-tight">No problems found</h4>
                                    <p className="text-xs text-slate-500 mt-1 capitalize">Try adjusting filters</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-300">
                                    {questions.map((q) => {
                                        const isSelected = selectedQuestion?.id === q.id;
                                        return (
                                            <button
                                                key={q.id}
                                                onClick={() => setSelectedQuestion(q)}
                                                className={`w-full text-left px-4 py-4 transition-all flex items-center group bg-accent`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <h4
                                                        title={q.title}
                                                        className={`font-semibold truncate text-sm transition-colors ${isSelected ? 'text-emerald-600' : 'text-slate-800'}`}
                                                    >
                                                        {q.title}
                                                    </h4>
                                                </div>
                                                
                                                <div className="w-32 px-4">
                                                    <span className={`text-xs font-medium capitalize transition-colors ${q.difficulty === 'easy' ? 'text-green-600' : q.difficulty === 'medium' ? 'text-yellow-600' : 'text-red-600'}`}>
                                                        {q.difficulty}
                                                    </span>
                                                </div>

                                                <div className="w-40 px-4 truncate">
                                                    <span className="text-[11px] font-medium capitalize text-muted-foreground">
                                                        {q.topic || 'General'}
                                                    </span>
                                                </div>

                                                <div className="w-12 flex justify-end">
                                                    {isSelected ? (
                                                        <div className="w-6 h-6 flex items-center justify-center animate-in zoom-in duration-200">
                                                            <FiCheck className="text-emerald-600 text-sm" strokeWidth={4} />
                                                        </div>
                                                    ) : (
                                                        <div className="w-6 h-6 rounded-full flex items-center justify-center transition-all">
                                                            <FiPlus className="text-muted-foreground group-hover:text-emerald-600 text-sm" strokeWidth={3} />
                                                        </div>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Panel: Selected Question Preview */}
                    <div className="w-full md:w-2/5 flex flex-col bg-white shrink-0">
                        {selectedQuestion ? (
                            <div className="flex flex-col h-full animate-in slide-in-from-right-4 duration-300">
                                <div className="px-6 py-8 border-b border-gray-100">
                                    <h4 className="text-xl font-bold text-foreground leading-tight text-left mb-2">{selectedQuestion.title}</h4>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className={`text-xs font-semibold capitalize ${getDifficultyColor(selectedQuestion.difficulty)}`}>
                                            {selectedQuestion.difficulty}
                                        </span>
                                        <span className='text-xs text-muted-foreground font-bold'>|</span>
                                        <span className="text-muted-foreground font-medium truncate text-xs">
                                            {selectedQuestion.topic || 'General'}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto px-6 py-4 mr-2 custom-scrollbar">
                                    <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap text-left font-medium">
                                        {selectedQuestion.description}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-center p-12 text-slate-500">
                                <h4 className="font-bold text-muted-foreground">No Selection</h4>
                                <p className="text-sm mt-2 max-w-[300px]">Select a problem to preview its details before assigning it</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-gray-300 flex justify-end items-center space-x-4 bg-emerald-50/10 rounded-b">
                    {error && (
                        <div className="flex-1 text-xs text-rose-600 font-bold px-4 truncate">
                            {error}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={assigning}
                        className="px-6 py-2.5 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAssignProblem}
                        disabled={assigning || !selectedQuestion}
                        className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white transition-all font-semibold rounded flex items-center disabled:opacity-50 text-sm"
                    >
                        {assigning ? (
                            <><span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></span> Assigning...</>
                        ) : "Assign Problem"}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AssignPracticeProblemModal;
